import { Loader2 } from "lucide-react";

import { cn } from "../lib/utils";

export type SpinnerSize = "xs" | "sm" | "md" | "lg";

export interface SpinnerProps {
  /** Icon size. `xs` for dense icon controls, `sm` inline, `md` default, `lg` for empty states. */
  size?: SpinnerSize;
  className?: string;
  /**
   * Text announced to assistive tech (and shown next to the icon when
   * present). Defaults to "Loading" for the accessible name only.
   */
  label?: string;
  /**
   * When the parent already carries the loading semantics (for example a
   * button with `aria-busy`), render the spinner as purely visual.
   */
  decorative?: boolean;
}

/** Pixel size of the `Loader2` glyph for each `SpinnerSize`. */
export const spinnerSizes: Readonly<Record<SpinnerSize, number>> = {
  xs: 12,
  sm: 14,
  md: 20,
  lg: 32,
};

/**
 * Utility spinner for compact, mechanical waits: retry buttons, dropdowns,
 * settings rows, table cells. Announces as a live `status` region unless
 * `decorative`. Use `Skeleton` when the loading content's shape is known.
 */
export function Spinner({ size = "md", className, label, decorative = false }: SpinnerProps) {
  return (
    <span
      data-slot="spinner"
      data-size={size}
      className={cn("inline-flex items-center gap-2", className)}
      role={decorative ? undefined : "status"}
      aria-label={decorative ? undefined : label ?? "Loading"}
      aria-hidden={decorative ? true : undefined}
    >
      <Loader2 size={spinnerSizes[size]} className="shrink-0 motion-safe:animate-spin" aria-hidden="true" />
      {label && <span className="text-sm text-muted-foreground">{label}</span>}
    </span>
  );
}
